import { useState } from 'react';
import { Loader2, Landmark, AlertTriangle, ChevronDown, ChevronRight } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { diagnosticsAPI } from '../../lib/api';
import { toast } from 'sonner';
import { getErrorMessage } from '../../lib/errors';
import { formatCompact } from '../../lib/chartFormatters';

export function PennylaneReconciliationPreviewPanel() {
  const [entityId, setEntityId] = useState('bi_sas');
  const [daysBack, setDaysBack] = useState(90);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const runCheck = async () => {
    setLoading(true);
    setResult(null);
    try {
      const { data } = await diagnosticsAPI.pennylaneReconciliationPreview({
        entity_id: entityId,
        days_back: daysBack,
      });
      setResult(data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <Landmark className="h-5 w-5 text-emerald-600" />
          Pennylane Reconciliation Preview
        </CardTitle>
        <p className="text-sm text-muted-foreground">
          Two-shape recognition engine: per-booking (1:1, Booking.com) and batch
          (1:N net sum, Airbnb). Read-only, no writes, no matching applied.
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Controls */}
        <div className="flex items-end gap-3 flex-wrap">
          <div>
            <label className="text-xs font-medium text-muted-foreground">Entity</label>
            <select
              className="block mt-1 border rounded px-2 py-1.5 text-sm bg-background"
              value={entityId}
              onChange={(e) => setEntityId(e.target.value)}
            >
              <option value="bi_sas">Bonjour Investments SAS (bi_sas)</option>
              <option value="bi_immo">Bonjour Immobilier (bi_immo)</option>
              <option value="afpv">AFPV Invest (afpv)</option>
              <option value="sci_113">SCI 113 Rue Saint-Honore (sci_113)</option>
              <option value="pfv">PFV Holding (pfv)</option>
              <option value="fan_zhang_piotr_kacki">Fan Zhang & Piotr Kacki</option>
            </select>
          </div>
          <div>
            <label className="text-xs font-medium text-muted-foreground">Days back</label>
            <input
              type="number"
              className="block mt-1 border rounded px-2 py-1.5 text-sm bg-background w-20"
              value={daysBack}
              onChange={(e) => setDaysBack(Number(e.target.value) || 90)}
              min={7}
              max={365}
            />
          </div>
          <Button onClick={runCheck} disabled={loading} size="sm">
            {loading ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
            Check
          </Button>
        </div>

        {/* Results */}
        {result && (
          <div className="space-y-4 mt-4">
            {/* Scope status badge (always first) */}
            <ScopeStatusBadge result={result} />

            {/* If scope is missing, show only the instruction */}
            {result.scope_status !== 'ok' && result.instruction && (
              <div className="border border-orange-300 bg-orange-50 dark:bg-orange-950/30 rounded p-3 text-sm text-orange-800 dark:text-orange-300">
                {result.instruction}
              </div>
            )}

            {/* Top-level error */}
            {result.error && (
              <div className="border border-red-300 bg-red-50 dark:bg-red-950/30 rounded p-3 text-sm text-red-700 dark:text-red-400">
                <AlertTriangle className="inline h-4 w-4 mr-1" />
                {result.error}
              </div>
            )}

            {/* Data sections (only when scope ok, no error) */}
            {result.scope_status === 'ok' && !result.error && (
              <>
                {/* Forward floor notice (BI Immo only) */}
                {result.forward_floor && (
                  <div className="border border-blue-200 bg-blue-50 dark:bg-blue-950/30 rounded px-3 py-1.5 text-xs text-blue-800 dark:text-blue-300">
                    Forward floor: payouts on/after {result.forward_floor}
                  </div>
                )}

                {/* Pool-integrity warning: Airbnb pool is 0 with payouts in window */}
                {result.pool_integrity_warning && (
                  <div className="border border-red-300 bg-red-50 dark:bg-red-950/30 rounded p-3 text-sm text-red-700 dark:text-red-400">
                    <AlertTriangle className="inline h-4 w-4 mr-1" />
                    {result.pool_integrity_warning}
                  </div>
                )}

                {/* Two recognised rates side by side */}
                {result.summary && (
                  <Section title="Summary">
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                      <MetricCard
                        label="Open invoices"
                        value={result.summary.open_invoices_considered}
                        sub={`+ ${result.summary.already_reconciled} already reconciled`}
                      />
                      <MetricCard
                        label="Bank transactions"
                        value={result.summary.bank_transactions_fetched}
                        sub={result.transaction_window ? `last ${result.transaction_window.days_back}d` : ''}
                      />
                      <MetricCard
                        label="Already reconciled"
                        value={result.summary.already_reconciled}
                      />
                      {result.legacy_non_system?.count > 0 && (
                        <MetricCard
                          label="Legacy (non-system)"
                          value={result.legacy_non_system.count}
                          sub={`${formatCompact(parseFloat(result.legacy_non_system.total_eur))} EUR`}
                        />
                      )}
                      {result.out_of_scope?.count > 0 && (
                        <MetricCard
                          label="Out of scope"
                          value={result.out_of_scope.count}
                          sub={`${formatCompact(parseFloat(result.out_of_scope.total_eur))} EUR (non-channel)`}
                        />
                      )}
                      {result.unclassified_transactions?.count > 0 && (
                        <MetricCard
                          label="Unclassified (channel)"
                          value={result.unclassified_transactions.count}
                          sub={`${formatCompact(parseFloat(result.unclassified_transactions.total_eur))} EUR, unrecognized label`}
                        />
                      )}
                    </div>

                    {/* Booking.com four-tier summary (+ already-reconciled-externally) */}
                    {result.summary.booking_com && (
                      <ChannelFourTiers
                        channelLabel="Booking.com"
                        stats={result.summary.booking_com}
                        anchorWindow={result.transaction_window?.bcom_checkout_window}
                        anchorMapSize={result.transaction_window?.bcom_checkout_map_size}
                        anchorLabel="checkout"
                        totalKey="total_booking_com_transactions"
                        entityId={entityId}
                        tiers={['matched_1to1', 'matched_batch', 'ambiguous', 'already_reconciled_externally', 'unmatched', 'excluded_no_checkout']}
                      />
                    )}

                    {/* Airbnb four-tier summary (+ already-reconciled-externally) */}
                    {result.summary.airbnb && (
                      <ChannelFourTiers
                        channelLabel="Airbnb"
                        stats={result.summary.airbnb}
                        anchorWindow={result.transaction_window?.airbnb_checkin_window}
                        anchorMapSize={result.transaction_window?.airbnb_checkin_map_size}
                        anchorLabel="check-in"
                        totalKey="total_airbnb_transactions"
                        borderColor="border-violet-300"
                        bgColor="bg-violet-50 dark:bg-violet-950/30"
                        entityId={entityId}
                        tiers={['matched_1to1', 'matched_batch', 'ambiguous', 'already_reconciled_externally', 'unmatched']}
                      />
                    )}
                  </Section>
                )}

                {/* Channel-ref trace (Booking.com) */}
                {result.channel_ref_trace && (
                  <ChannelRefTraceSection
                    trace={result.channel_ref_trace}
                    uplistingSample={result.uplisting_ext_id_sample}
                  />
                )}

                {/* Airbnb batch trace */}
                {result.airbnb_batch_trace && (
                  <AirbnbBatchTraceSection trace={result.airbnb_batch_trace} />
                )}

                {/* Proposed matches table */}
                {/* Owner feed summary (BI Immo union pool only) */}
                {result.owner_feed_summary?.length > 0 && (
                  <Section title="Owner Feed Pool (BI Immo Union)">
                    <div className="overflow-x-auto">
                      <table className="text-xs w-full border-collapse">
                        <thead>
                          <tr className="bg-muted/50">
                            <th className="px-2 py-1 text-left">Owner Entity</th>
                            <th className="px-2 py-1 text-left">Status</th>
                            <th className="px-2 py-1 text-right">Txns Added</th>
                          </tr>
                        </thead>
                        <tbody>
                          {result.owner_feed_summary.map((o, i) => (
                            <tr key={i} className="border-t border-muted">
                              <td className="px-2 py-1 font-mono">{o.entity_id}</td>
                              <td className="px-2 py-1">
                                <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${
                                  o.status === 'ok'
                                    ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                                    : 'bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200'
                                }`}>
                                  {o.status}
                                </span>
                              </td>
                              <td className="px-2 py-1 text-right font-mono">{o.txn_count}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </Section>
                )}

                {/* Proposed matches table */}
                {result.proposed_matches?.length > 0 && (
                  <Section title={`Proposed Matches (${result.proposed_matches.length})`}>
                    <div className="overflow-x-auto">
                      <table className="text-xs w-full border-collapse">
                        <thead>
                          <tr className="bg-muted/50">
                            <th className="px-2 py-1 text-left">Shape</th>
                            <th className="px-2 py-1 text-left">Txn ID</th>
                            <th className="px-2 py-1 text-right">Txn Amount</th>
                            <th className="px-2 py-1 text-left">Txn Label</th>
                            <th className="px-2 py-1 text-left">Invoice(s)</th>
                            <th className="px-2 py-1 text-center">Confidence</th>
                            <th className="px-2 py-1 text-left">Match Field</th>
                            {result.owner_feed_summary && (
                              <th className="px-2 py-1 text-left">Source Feed</th>
                            )}
                          </tr>
                        </thead>
                        <tbody>
                          {result.proposed_matches.map((m, i) => (
                            <MatchRow key={i} match={m} showSource={!!result.owner_feed_summary} />
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </Section>
                )}

                {/* Unmatched invoices */}
                {result.unmatched_invoices?.length > 0 && (
                  <CollapsibleSection
                    title={`Unmatched Invoices (${result.unmatched_invoices.length})`}
                  >
                    <div className="overflow-x-auto">
                      <table className="text-xs w-full border-collapse">
                        <thead>
                          <tr className="bg-muted/50">
                            <th className="px-2 py-1 text-left">Invoice #</th>
                            <th className="px-2 py-1 text-right">Amount</th>
                            <th className="px-2 py-1 text-left">Currency</th>
                            <th className="px-2 py-1 text-left">Channel</th>
                            <th className="px-2 py-1 text-left">Date</th>
                          </tr>
                        </thead>
                        <tbody>
                          {result.unmatched_invoices.map((inv, i) => (
                            <tr key={i} className="border-t border-muted">
                              <td className="px-2 py-1 font-mono">{inv.invoice_number || '-'}</td>
                              <td className="px-2 py-1 text-right font-mono">{inv.invoice_amount}</td>
                              <td className="px-2 py-1">{inv.currency}</td>
                              <td className="px-2 py-1">{inv.channel || '-'}</td>
                              <td className="px-2 py-1">{inv.date || '-'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </CollapsibleSection>
                )}

                {/* Out-of-scope invoices (non-channel, not matched against payouts) */}
                {result.out_of_scope?.count > 0 && (
                  <CollapsibleSection
                    title={`Out of Scope (${result.out_of_scope.count} invoices, ${formatCompact(parseFloat(result.out_of_scope.total_eur))} EUR)`}
                  >
                    <p className="text-xs text-muted-foreground mb-2">
                      Invoices whose channel is not Airbnb or Booking.com. Not reconciled against bank payouts.
                    </p>
                    <div className="overflow-x-auto">
                      <table className="text-xs w-full border-collapse">
                        <thead>
                          <tr className="bg-muted/50">
                            <th className="px-2 py-1 text-left">Invoice #</th>
                            <th className="px-2 py-1 text-right">Amount</th>
                            <th className="px-2 py-1 text-left">Currency</th>
                            <th className="px-2 py-1 text-left">Channel</th>
                            <th className="px-2 py-1 text-left">Reason</th>
                            <th className="px-2 py-1 text-left">Date</th>
                          </tr>
                        </thead>
                        <tbody>
                          {result.out_of_scope.invoices.map((inv, i) => (
                            <tr key={i} className="border-t border-muted">
                              <td className="px-2 py-1 font-mono">{inv.invoice_number || '-'}</td>
                              <td className="px-2 py-1 text-right font-mono">{inv.invoice_amount}</td>
                              <td className="px-2 py-1">{inv.currency}</td>
                              <td className="px-2 py-1">{inv.channel || '-'}</td>
                              <td className="px-2 py-1 text-muted-foreground">{inv.reason || '-'}</td>
                              <td className="px-2 py-1">{inv.date || '-'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </CollapsibleSection>
                )}

                {/* Legacy non-system invoices (not raised by BR OS) */}
                {result.legacy_non_system?.count > 0 && (
                  <CollapsibleSection
                    title={`Legacy Non-System (${result.legacy_non_system.count} invoices, ${formatCompact(parseFloat(result.legacy_non_system.total_eur))} EUR)`}
                  >
                    <p className="text-xs text-muted-foreground mb-2">
                      Invoices not raised by the system (no uplisting external reference). These are legacy invoices with no booking link by construction.
                    </p>
                    <div className="overflow-x-auto">
                      <table className="text-xs w-full border-collapse">
                        <thead>
                          <tr className="bg-muted/50">
                            <th className="px-2 py-1 text-left">Invoice #</th>
                            <th className="px-2 py-1 text-right">Amount</th>
                            <th className="px-2 py-1 text-left">Currency</th>
                            <th className="px-2 py-1 text-left">Channel</th>
                            <th className="px-2 py-1 text-left">Date</th>
                          </tr>
                        </thead>
                        <tbody>
                          {result.legacy_non_system.invoices.map((inv, i) => (
                            <tr key={i} className="border-t border-muted">
                              <td className="px-2 py-1 font-mono">{inv.invoice_number || '-'}</td>
                              <td className="px-2 py-1 text-right font-mono">{inv.invoice_amount}</td>
                              <td className="px-2 py-1">{inv.currency}</td>
                              <td className="px-2 py-1">{inv.channel || '-'}</td>
                              <td className="px-2 py-1">{inv.date || '-'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </CollapsibleSection>
                )}

                {/* Unmatched transactions */}
                {result.unmatched_transactions?.length > 0 && (
                  <CollapsibleSection
                    title={`Unmatched Transactions (${result.unmatched_transactions.length})`}
                  >
                    <div className="overflow-x-auto">
                      <table className="text-xs w-full border-collapse">
                        <thead>
                          <tr className="bg-muted/50">
                            <th className="px-2 py-1 text-left">Txn ID</th>
                            <th className="px-2 py-1 text-right">Amount</th>
                            <th className="px-2 py-1 text-left">Currency</th>
                            <th className="px-2 py-1 text-left">Label</th>
                            <th className="px-2 py-1 text-left">Date</th>
                            <th className="px-2 py-1 text-center">Airbnb?</th>
                            <th className="px-2 py-1 text-center">Booking.com?</th>
                          </tr>
                        </thead>
                        <tbody>
                          {result.unmatched_transactions.slice(0, 50).map((txn, i) => (
                            <tr key={i} className="border-t border-muted">
                              <td className="px-2 py-1 font-mono">{txn.transaction_id}</td>
                              <td className="px-2 py-1 text-right font-mono">{txn.transaction_amount}</td>
                              <td className="px-2 py-1">{txn.currency}</td>
                              <td className="px-2 py-1 max-w-[250px] truncate" title={txn.transaction_label}>
                                {txn.transaction_label || '-'}
                              </td>
                              <td className="px-2 py-1">{txn.transaction_date || '-'}</td>
                              <td className="px-2 py-1 text-center">
                                {txn.is_airbnb_labelled ? <Badge className="bg-pink-600 text-white text-[10px]">airbnb</Badge> : null}
                              </td>
                              <td className="px-2 py-1 text-center">
                                {txn.is_booking_com_labelled ? <Badge className="bg-sky-600 text-white text-[10px]">booking.com</Badge> : null}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      {result.unmatched_transactions.length > 50 && (
                        <p className="text-xs text-muted-foreground mt-1 px-2">
                          Showing first 50 of {result.unmatched_transactions.length}.
                        </p>
                      )}
                    </div>
                  </CollapsibleSection>
                )}

                {/* Unclassified transactions: label matched neither channel
                    recognizer at all (a recognizer gap, distinct from a
                    recognized-channel transaction the matcher failed to
                    pair -- see the Unmatched Transactions section above). */}
                {result.unclassified_transactions?.count > 0 && (
                  <CollapsibleSection
                    title={`Unclassified Channel (${result.unclassified_transactions.count} transactions, ${formatCompact(parseFloat(result.unclassified_transactions.total_eur))} EUR)`}
                  >
                    <p className="text-xs text-muted-foreground mb-2">
                      Bank transactions whose label matched neither the Airbnb nor the
                      Booking.com recognizer. Not a matching failure -- the recognizer
                      never had an opinion. Census these labels to find channel
                      variants the pattern should cover.
                    </p>
                    <div className="overflow-x-auto">
                      <table className="text-xs w-full border-collapse">
                        <thead>
                          <tr className="bg-muted/50">
                            <th className="px-2 py-1 text-left">Txn ID</th>
                            <th className="px-2 py-1 text-right">Amount</th>
                            <th className="px-2 py-1 text-left">Currency</th>
                            <th className="px-2 py-1 text-left">Label</th>
                            <th className="px-2 py-1 text-left">Date</th>
                          </tr>
                        </thead>
                        <tbody>
                          {result.unclassified_transactions.rows.slice(0, 50).map((txn, i) => (
                            <tr key={i} className="border-t border-muted">
                              <td className="px-2 py-1 font-mono">{txn.transaction_id}</td>
                              <td className="px-2 py-1 text-right font-mono">{txn.transaction_amount}</td>
                              <td className="px-2 py-1">{txn.currency}</td>
                              <td className="px-2 py-1 max-w-[300px] truncate" title={txn.transaction_label}>
                                {txn.transaction_label || '-'}
                              </td>
                              <td className="px-2 py-1">{txn.transaction_date || '-'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      {result.unclassified_transactions.rows.length > 50 && (
                        <p className="text-xs text-muted-foreground mt-1 px-2">
                          Showing first 50 of {result.unclassified_transactions.rows.length}.
                        </p>
                      )}
                    </div>
                  </CollapsibleSection>
                )}
              </>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

/* ── Booking.com four-tier component ─────────────────────────────── */

const _TIER_META = {
  matched_1to1:  { label: 'Matched 1:1',    color: 'bg-green-600',  textColor: 'text-green-700 dark:text-green-400' },
  matched_batch: { label: 'Matched Batch',   color: 'bg-blue-600',   textColor: 'text-blue-700 dark:text-blue-400' },
  ambiguous:     { label: 'Ambiguous',        color: 'bg-orange-500', textColor: 'text-orange-700 dark:text-orange-400' },
  already_reconciled_externally: {
    label: 'Already Reconciled (external)', color: 'bg-teal-600', textColor: 'text-teal-700 dark:text-teal-400',
  },
  unmatched:     { label: 'Unmatched',        color: 'bg-red-600',    textColor: 'text-red-700 dark:text-red-400' },
  excluded_no_checkout: {
    label: 'Excluded (no checkout)', color: 'bg-amber-500', textColor: 'text-amber-700 dark:text-amber-400',
  },
};

const _DEFAULT_TIERS = ['matched_1to1', 'matched_batch', 'ambiguous', 'unmatched'];

function ChannelFourTiers({
  channelLabel,
  stats,
  anchorWindow,
  anchorMapSize,
  anchorLabel = 'anchor',
  totalKey = 'total_transactions',
  borderColor = 'border-emerald-300',
  bgColor = 'bg-emerald-50 dark:bg-emerald-950/30',
  entityId,
  tiers = _DEFAULT_TIERS,
}) {
  if (!stats) return null;
  const total = stats[totalKey] || 0;
  const tol = stats.tolerance;
  const gridCols = tiers.length >= 5 ? 'md:grid-cols-5' : 'md:grid-cols-4';

  return (
    <div className={`border rounded-lg p-3 mt-3 ${borderColor} ${bgColor}`}>
      <p className="text-xs font-semibold mb-2">{channelLabel} ({total} transactions)</p>
      <div className={`grid grid-cols-2 ${gridCols} gap-2`}>
        {tiers.map((tier) => {
          const data = stats[tier];
          const meta = _TIER_META[tier];
          if (!data) return null;
          return (
            <TierBlock key={tier} tier={tier} data={data} meta={meta} entityId={entityId} />
          );
        })}
      </div>
      <div className="mt-2 space-y-0.5">
        {tol && (
          <p className="text-[10px] text-muted-foreground">
            Tolerance: {tol.description} (floor {tol.floor_eur} EUR, {tol.pct}%)
          </p>
        )}
        {anchorWindow && (
          <p className="text-[10px] text-muted-foreground">
            Date window: {anchorWindow}
            {anchorMapSize != null ? ` (${anchorMapSize} invoices with ${anchorLabel})` : ''}
          </p>
        )}
      </div>
    </div>
  );
}


function TierBlock({ tier, data, meta, entityId }) {
  const [open, setOpen] = useState(false);
  const amt = parseFloat(data.amount || '0');
  const rows = data.matches || data.rows || [];
  const expandable = rows.length > 0;
  const isApplyable = (entityId === 'afpv' || entityId === 'bi_immo') && (tier === 'matched_1to1' || tier === 'matched_batch');

  return (
    <div className="border rounded p-2 bg-background">
      <div className="flex items-center justify-between">
        <p className="text-[10px] text-muted-foreground">{meta.label}</p>
        {expandable && (
          <button
            className="text-[10px] text-blue-600 dark:text-blue-400 underline"
            onClick={() => setOpen(!open)}
          >
            {open ? 'hide' : 'show'}
          </button>
        )}
      </div>
      <p className={`text-lg font-bold ${meta.textColor}`}>{data.count}</p>
      <p className="text-[10px] text-muted-foreground font-mono">
        {formatCompact(amt, 'EUR')}
      </p>
      {open && expandable && (
        <div className="mt-2 overflow-x-auto">
          {tier === 'unmatched' ? (
            <TierUnmatchedTable rows={rows} />
          ) : tier === 'already_reconciled_externally' ? (
            <TierAlreadyReconciledTable rows={rows} />
          ) : tier === 'excluded_no_checkout' ? (
            <TierExcludedNoCheckoutTable rows={rows} />
          ) : isApplyable ? (
            <ApplyableTierMatchTable
              matches={rows}
              isBatch={tier === 'matched_batch'}
              entityId={entityId}
            />
          ) : (
            <TierMatchTable matches={rows} isBatch={tier === 'matched_batch'} />
          )}
        </div>
      )}
    </div>
  );
}

function TierMatchTable({ matches, isBatch }) {
  // Only show the Source column when there are cross-entity matches
  // (i.e., union pool data worth surfacing). All-same-entity is noise.
  const hasSource = matches.some((m) => m.source_classification === 'cross_entity' || m.cross_feed_ambiguous);
  return (
    <table className="text-[10px] w-full border-collapse">
      <thead>
        <tr className="bg-muted/50">
          <th className="px-1 py-0.5 text-left">Txn ID</th>
          <th className="px-1 py-0.5 text-right">Payout</th>
          <th className="px-1 py-0.5 text-left">{isBatch ? 'Invoices' : 'Invoice'}</th>
          <th className="px-1 py-0.5 text-left">Field</th>
          {hasSource && <th className="px-1 py-0.5 text-left">Source</th>}
        </tr>
      </thead>
      <tbody>
        {matches.map((m, i) => {
          const isCross = m.source_classification === 'cross_entity';
          const isAmbiguous = m.cross_feed_ambiguous;
          return (
            <tr key={i} className={`border-t border-muted${isAmbiguous ? ' bg-red-50 dark:bg-red-950/30' : isCross ? ' bg-amber-50 dark:bg-amber-950/30' : ''}`}>
              <td className="px-1 py-0.5 font-mono">{m.transaction_id}</td>
              <td className="px-1 py-0.5 text-right font-mono">{m.transaction_amount}</td>
              <td className="px-1 py-0.5 font-mono">
                {(m.member_invoices || []).map((inv) =>
                  inv.invoice_number || inv.invoice_id
                ).join(', ') || '-'}
              </td>
              <td className="px-1 py-0.5 text-muted-foreground">{m.match_field}</td>
              {hasSource && (
                <td className="px-1 py-0.5">
                  <span className={`px-1 py-0.5 rounded font-medium ${
                    isAmbiguous
                      ? 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                      : isCross
                        ? 'bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200'
                        : 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                  }`}>
                    {isAmbiguous ? 'ambiguous' : isCross ? 'cross' : 'same'}
                  </span>
                  <span className="ml-1 text-muted-foreground">
                    {isAmbiguous
                      ? (m.colliding_feeds || []).join(', ')
                      : m.source_entity_name || m.source_entity_id || ''}
                  </span>
                  {isCross && (
                    <span className="ml-1 text-amber-700 dark:text-amber-400 text-[9px]">
                      handled by accountant
                    </span>
                  )}
                  {isAmbiguous && !isCross && (
                    <span className="ml-1 text-red-600 dark:text-red-400 font-medium">
                      (no apply)
                    </span>
                  )}
                </td>
              )}
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

/* ── Applyable match table with checkboxes + apply/undo ─────────── */
/* Used for AFPV (all same_entity) and BI Immo (mixed pool: only
   same_entity rows are tickable; cross_entity and ambiguous are
   display-only within the same table). */

function ApplyableTierMatchTable({ matches, isBatch, entityId }) {
  // A row is applyable only if the matcher asserted same_entity.
  // cross_entity is accountant-owned; ambiguous has no classification.
  const isRowApplyable = (m) => m.source_classification === 'same_entity';

  // State: which rows are checked, keyed by "txn_id:inv_id"
  const [checked, setChecked] = useState(() => {
    const init = {};
    matches.forEach((m) => {
      if (!isRowApplyable(m)) return;
      (m.member_invoices || []).forEach((inv) => {
        const key = `${m.transaction_id}:${inv.invoice_id}`;
        // Pre-check HIGH confidence only; LOW and ambiguous start unchecked
        init[key] = m.confidence === 'high';
      });
    });
    return init;
  });

  // State: per-row apply status, keyed by "txn_id:inv_id"
  // Values: null (not applied), 'applying', 'applied', 'apply_partial', 'failed',
  //         'undoing', 'unapplied'
  const [rowStatus, setRowStatus] = useState({});
  // Per-row warnings from Pennylane
  const [rowWarnings, setRowWarnings] = useState({});
  // Global applying flag
  const [applying, setApplying] = useState(false);

  const toggleCheck = (key) => {
    // Cannot toggle rows that are already applied
    if (rowStatus[key] === 'applied' || rowStatus[key] === 'apply_partial') return;
    setChecked((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const checkedCount = Object.values(checked).filter(Boolean).length;
  const unappliedChecked = Object.entries(checked).filter(
    ([key, val]) => val && (!rowStatus[key] || rowStatus[key] === 'failed')
  );

  const handleApply = async () => {
    if (unappliedChecked.length === 0) return;
    setApplying(true);

    for (const [key] of unappliedChecked) {
      const [txnId, invId] = key.split(':');
      // Find the match to get confidence and match_field
      let confidence = 'unknown';
      let matchField = 'unknown';
      for (const m of matches) {
        if (String(m.transaction_id) === txnId) {
          confidence = m.confidence || 'unknown';
          matchField = m.match_field || 'unknown';
          break;
        }
      }

      setRowStatus((prev) => ({ ...prev, [key]: 'applying' }));
      try {
        const { data } = await diagnosticsAPI.pennylaneReconApplyMatch({
          entity_id: entityId,
          pennylane_transaction_id: Number(txnId),
          pennylane_invoice_id: Number(invId),
          confidence,
          match_field: matchField,
          source_classification: 'same_entity',
        });
        setRowStatus((prev) => ({ ...prev, [key]: data.status || 'applied' }));
        if (data.warnings?.length) {
          setRowWarnings((prev) => ({ ...prev, [key]: data.warnings }));
        }
      } catch (err) {
        setRowStatus((prev) => ({ ...prev, [key]: 'failed' }));
        setRowWarnings((prev) => ({
          ...prev,
          [key]: [getErrorMessage(err)],
        }));
      }
    }
    setApplying(false);
  };

  const handleUndo = async (key) => {
    const [txnId, invId] = key.split(':');
    setRowStatus((prev) => ({ ...prev, [key]: 'undoing' }));
    try {
      const { data } = await diagnosticsAPI.pennylaneReconUnapplyMatch({
        entity_id: entityId,
        pennylane_transaction_id: Number(txnId),
        pennylane_invoice_id: Number(invId),
      });
      setRowStatus((prev) => ({ ...prev, [key]: null }));
      setRowWarnings((prev) => {
        const next = { ...prev };
        if (data.warnings?.length) {
          next[key] = data.warnings;
        } else {
          delete next[key];
        }
        return next;
      });
      // Uncheck after undo so it doesn't auto-reapply
      setChecked((prev) => ({ ...prev, [key]: false }));
    } catch (err) {
      setRowStatus((prev) => ({ ...prev, [key]: 'applied' }));
      toast.error(`Undo failed: ${getErrorMessage(err)}`);
    }
  };

  const statusBadge = (key) => {
    const s = rowStatus[key];
    if (!s) return null;
    const styles = {
      applying: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400',
      applied: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
      already_applied: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
      apply_partial: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400',
      failed: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
      undoing: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400',
    };
    const labels = {
      applying: 'Applying...',
      applied: 'Reconciled',
      already_applied: 'Reconciled',
      apply_partial: 'Partial',
      failed: 'Failed',
      undoing: 'Undoing...',
    };
    return (
      <span className={`inline-block px-1 py-0 rounded text-[9px] font-medium ${styles[s] || ''}`}>
        {labels[s] || s}
      </span>
    );
  };

  return (
    <div>
      <table className="text-[10px] w-full border-collapse">
        <thead>
          <tr className="bg-muted/50">
            <th className="px-1 py-0.5 text-center w-6"></th>
            <th className="px-1 py-0.5 text-left">Txn ID</th>
            <th className="px-1 py-0.5 text-right">Payout</th>
            <th className="px-1 py-0.5 text-left">{isBatch ? 'Invoices' : 'Invoice'}</th>
            <th className="px-1 py-0.5 text-left">Field</th>
            <th className="px-1 py-0.5 text-left">Status</th>
          </tr>
        </thead>
        <tbody>
          {matches.map((m, i) => {
            const invoices = m.member_invoices || [];
            // For 1:1, one row per match. For batch with multiple invoices,
            // one row per match (shows all invoices, single checkbox).
            // The apply iterates member_invoices internally.
            const invId = invoices.length === 1 ? invoices[0].invoice_id : invoices[0]?.invoice_id;
            const key = `${m.transaction_id}:${invId}`;
            const applyable = isRowApplyable(m);
            const isCross = m.source_classification === 'cross_entity';
            const isAmbig = m.cross_feed_ambiguous;
            const isApplied = rowStatus[key] === 'applied' || rowStatus[key] === 'already_applied';
            const isProcessing = rowStatus[key] === 'applying' || rowStatus[key] === 'undoing';
            const warns = rowWarnings[key];

            return (
              <tr key={i} className={`border-t border-muted ${isApplied ? 'bg-green-50 dark:bg-green-950/20' : isCross ? 'bg-amber-50 dark:bg-amber-950/30' : isAmbig ? 'bg-red-50 dark:bg-red-950/30' : ''}`}>
                <td className="px-1 py-0.5 text-center">
                  {applyable ? (
                    <input
                      type="checkbox"
                      className="h-3 w-3"
                      checked={!!checked[key] || isApplied}
                      disabled={isApplied || isProcessing}
                      onChange={() => toggleCheck(key)}
                    />
                  ) : null}
                </td>
                <td className="px-1 py-0.5 font-mono">{m.transaction_id}</td>
                <td className="px-1 py-0.5 text-right font-mono">{m.transaction_amount}</td>
                <td className="px-1 py-0.5 font-mono">
                  {invoices.map((inv) =>
                    inv.invoice_number || inv.invoice_id
                  ).join(', ') || '-'}
                </td>
                <td className="px-1 py-0.5 text-muted-foreground">{m.match_field}</td>
                <td className="px-1 py-0.5">
                  <div className="flex items-center gap-1">
                    {applyable ? (
                      <>
                        {statusBadge(key)}
                        {(isApplied || rowStatus[key] === 'apply_partial') && (
                          <button
                            className="text-[9px] text-red-600 dark:text-red-400 underline"
                            onClick={() => handleUndo(key)}
                            disabled={isProcessing}
                          >
                            Undo
                          </button>
                        )}
                        {warns && warns.length > 0 && (
                          <span
                            className="text-[9px] text-orange-600 cursor-help"
                            title={warns.join('\n')}
                          >
                            <AlertTriangle className="inline h-3 w-3" />
                          </span>
                        )}
                      </>
                    ) : isCross ? (
                      <span className="text-[9px] text-amber-700 dark:text-amber-400">
                        handled by accountant
                      </span>
                    ) : isAmbig ? (
                      <span className="text-[9px] text-red-600 dark:text-red-400">
                        (no apply)
                      </span>
                    ) : null}
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <div className="mt-2 flex items-center gap-2">
        <Button
          size="sm"
          className="h-6 text-[10px] px-2"
          disabled={unappliedChecked.length === 0 || applying}
          onClick={handleApply}
        >
          {applying ? (
            <Loader2 className="h-3 w-3 animate-spin mr-1" />
          ) : null}
          Apply {unappliedChecked.length > 0 ? `(${unappliedChecked.length})` : ''} confirmed matches
        </Button>
        {unappliedChecked.length === 0 && !applying && (
          <span className="text-[10px] text-muted-foreground">
            Tick rows to apply
          </span>
        )}
      </div>
    </div>
  );
}

function TierUnmatchedTable({ rows }) {
  return (
    <table className="text-[10px] w-full border-collapse">
      <thead>
        <tr className="bg-muted/50">
          <th className="px-1 py-0.5 text-left">Txn ID</th>
          <th className="px-1 py-0.5 text-right">Amount</th>
          <th className="px-1 py-0.5 text-left">Label</th>
          <th className="px-1 py-0.5 text-left">Date</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((r, i) => (
          <tr key={i} className="border-t border-muted">
            <td className="px-1 py-0.5 font-mono">{r.transaction_id}</td>
            <td className="px-1 py-0.5 text-right font-mono">{r.transaction_amount}</td>
            <td className="px-1 py-0.5 max-w-[200px] truncate" title={r.transaction_label}>
              {r.transaction_label || '-'}
            </td>
            <td className="px-1 py-0.5">{r.transaction_date || '-'}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function TierAlreadyReconciledTable({ rows }) {
  return (
    <table className="text-[10px] w-full border-collapse">
      <thead>
        <tr className="bg-muted/50">
          <th className="px-1 py-0.5 text-left">Txn ID</th>
          <th className="px-1 py-0.5 text-right">Payout</th>
          <th className="px-1 py-0.5 text-left">Matched Invoice</th>
          <th className="px-1 py-0.5 text-left">Invoice Date</th>
          <th className="px-1 py-0.5 text-right">Lag</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((r, i) => (
          <tr key={i} className="border-t border-muted">
            <td className="px-1 py-0.5 font-mono">{r.transaction_id}</td>
            <td className="px-1 py-0.5 text-right font-mono">{r.transaction_amount}</td>
            <td className="px-1 py-0.5 font-mono">{r.matched_invoice_number || '-'}</td>
            <td className="px-1 py-0.5 font-mono">{r.matched_invoice_date || '-'}</td>
            <td className="px-1 py-0.5 text-right font-mono">
              {r.lag_days != null ? `${r.lag_days}d` : '-'}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function TierExcludedNoCheckoutTable({ rows }) {
  return (
    <table className="text-[10px] w-full border-collapse">
      <thead>
        <tr className="bg-muted/50">
          <th className="px-1 py-0.5 text-left">Invoice #</th>
          <th className="px-1 py-0.5 text-right">Amount</th>
          <th className="px-1 py-0.5 text-left">Uplisting ID</th>
          <th className="px-1 py-0.5 text-left">Reason</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((r, i) => (
          <tr key={i} className="border-t border-muted">
            <td className="px-1 py-0.5 font-mono">{r.invoice_number || '-'}</td>
            <td className="px-1 py-0.5 text-right font-mono">{r.invoice_amount || '-'}</td>
            <td className="px-1 py-0.5 font-mono">{r.uplisting_id || '-'}</td>
            <td className="px-1 py-0.5">
              {r.reason === 'reservation_not_found' ? 'no reservation doc' : 'no checkout, no nights'}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

/* ── Sub-components ──────────────────────────────────────────────── */

function MatchRow({ match: m, showSource }) {
  const [expanded, setExpanded] = useState(false);
  const members = m.member_invoices || [];
  const hasMembers = members.length > 1;
  const isCrossEntity = m.source_classification === 'cross_entity';
  const isAmbiguous = m.cross_feed_ambiguous;

  return (
    <>
      <tr className={`border-t border-muted${isAmbiguous ? ' bg-red-50 dark:bg-red-950/30' : isCrossEntity ? ' bg-amber-50 dark:bg-amber-950/30' : ''}`}>
        <td className="px-2 py-1">
          <ShapeBadge shape={m.shape} />
        </td>
        <td className="px-2 py-1 font-mono">{m.transaction_id}</td>
        <td className="px-2 py-1 text-right font-mono">{m.transaction_amount}</td>
        <td className="px-2 py-1 max-w-[180px] truncate" title={m.transaction_label}>
          {m.transaction_label || '-'}
        </td>
        <td className="px-2 py-1">
          {hasMembers ? (
            <button
              className="text-blue-600 dark:text-blue-400 underline text-xs"
              onClick={() => setExpanded(!expanded)}
            >
              {members.length} invoices {expanded ? '(hide)' : '(show)'}
            </button>
          ) : members.length === 1 ? (
            <span className="font-mono">
              {members[0].invoice_number || members[0].invoice_id} ({members[0].invoice_amount})
            </span>
          ) : (
            <span className="text-muted-foreground">
              {m.candidate_count ? `${m.candidate_count} candidates` : '-'}
            </span>
          )}
        </td>
        <td className="px-2 py-1 text-center">
          <ConfidenceBadge confidence={m.confidence} />
        </td>
        <td className="px-2 py-1 text-xs text-muted-foreground">{m.match_field}</td>
        {showSource && (
          <td className="px-2 py-1">
            <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${
              isAmbiguous
                ? 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                : isCrossEntity
                  ? 'bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200'
                  : 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
            }`}>
              {isAmbiguous ? 'ambiguous' : isCrossEntity ? 'cross' : 'same'}
            </span>
            <span className="ml-1 text-[10px] text-muted-foreground">
              {isAmbiguous
                ? (m.colliding_feeds || []).join(', ')
                : m.source_entity_name || m.source_entity_id || ''}
            </span>
            {isCrossEntity && (
              <span className="ml-1 text-[10px] text-amber-700 dark:text-amber-400">
                handled by accountant
              </span>
            )}
            {isAmbiguous && !isCrossEntity && (
              <span className="ml-1 text-[10px] text-red-600 dark:text-red-400 font-medium">
                (no apply)
              </span>
            )}
          </td>
        )}
      </tr>
      {/* Expanded member invoices for batch matches */}
      {expanded && members.map((inv, j) => (
        <tr key={`member-${j}`} className="bg-muted/30">
          <td className="px-2 py-1" />
          <td className="px-2 py-1" />
          <td className="px-2 py-1" />
          <td className="px-2 py-1 text-[10px] text-muted-foreground pl-4">member {j + 1}</td>
          <td className="px-2 py-1 font-mono text-xs">
            {inv.invoice_number || inv.invoice_id} ({inv.invoice_amount})
          </td>
          <td className="px-2 py-1 text-[10px] text-muted-foreground">{inv.invoice_date || ''}</td>
          <td className="px-2 py-1" />
          {showSource && <td className="px-2 py-1" />}
        </tr>
      ))}
    </>
  );
}

const _STATUS_COLORS = {
  resolved: 'bg-green-600 text-white',
  parse_failed: 'bg-red-600 text-white',
  no_reservation: 'bg-amber-600 text-white',
  no_invoice: 'bg-orange-500 text-white',
  no_ref_in_label: 'bg-blue-600 text-white',
};

const _AIRBNB_TRACE_STATUS_COLORS = {
  matched: 'bg-green-600 text-white',
  already_reconciled_externally: 'bg-teal-600 text-white',
  no_candidates_in_window: 'bg-red-600 text-white',
  no_subset_sums_to_payout: 'bg-amber-600 text-white',
  ambiguous_subset_sum: 'bg-orange-500 text-white',
  ambiguous_1to1: 'bg-orange-500 text-white',
  too_many_candidates: 'bg-purple-600 text-white',
  no_amount: 'bg-gray-500 text-white',
  candidate_amount_unparseable: 'bg-gray-500 text-white',
};

function ChannelRefTraceSection({ trace: data, uplistingSample }) {
  const [open, setOpen] = useState(false);

  if (!data) return null;
  const { trace = [], status_counts = {}, pool_empty } = data;

  const summaryParts = ['no_ref_in_label', 'parse_failed', 'no_reservation', 'no_invoice', 'resolved']
    .map((k) => `${k}: ${status_counts[k] ?? 0}`)
    .join('  ');

  // Compute token-identity summary when both sides are available.
  // Filter overlap to Booking.com channel only so Airbnb IDs (different
  // format) do not dilute the count.
  const idSegments = trace.map((r) => r.id_segment).filter(Boolean);
  const parsedRefs = trace.map((r) => r.parsed_ref).filter(Boolean);
  const bookingSampleIds = (uplistingSample?.sample || [])
    .filter((s) => /booking/i.test(s.channel || ''))
    .map((s) => s.external_reservation_id)
    .filter((v) => v != null)
    .map(String);
  const sampleIdSet = new Set(bookingSampleIds);

  const idSegOverlap = idSegments.filter((v) => sampleIdSet.has(v)).length;
  const refOverlap = parsedRefs.filter((v) => sampleIdSet.has(v)).length;

  const avgIdSegLen = idSegments.length
    ? (idSegments.reduce((a, b) => a + b.length, 0) / idSegments.length).toFixed(0)
    : null;
  const avgRefLen = parsedRefs.length
    ? (parsedRefs.reduce((a, b) => a + b.length, 0) / parsedRefs.length).toFixed(0)
    : null;
  const avgSampleLen = bookingSampleIds.length
    ? (bookingSampleIds.reduce((a, b) => a + b.length, 0) / bookingSampleIds.length).toFixed(0)
    : null;

  let verdictLine = null;
  if (idSegments.length > 0 && bookingSampleIds.length > 0) {
    if (idSegOverlap > refOverlap) {
      verdictLine = `id_segment matches better: ${idSegOverlap} overlap vs ${refOverlap} for parsed_ref ` +
        `(against ${bookingSampleIds.length} Booking.com reservations). ` +
        `Avg digit length: id_segment=${avgIdSegLen}, uplisting=${avgSampleLen}, parsed_ref=${avgRefLen}. ` +
        `Parser should extract the /ID. token, not the ref: token.`;
    } else if (refOverlap > idSegOverlap) {
      verdictLine = `parsed_ref matches better: ${refOverlap} overlap vs ${idSegOverlap} for id_segment ` +
        `(against ${bookingSampleIds.length} Booking.com reservations). ` +
        `The current parser target appears correct.`;
    } else {
      verdictLine = `No overlap difference (id_segment=${idSegOverlap}, parsed_ref=${refOverlap}, ` +
        `${bookingSampleIds.length} Booking.com reservations). ` +
        `Avg digit length: id_segment=${avgIdSegLen}, uplisting=${avgSampleLen}, parsed_ref=${avgRefLen}. ` +
        `Compare lengths and values visually.`;
    }
  } else if (idSegments.length === 0) {
    verdictLine = 'No /ID. segments found in any label. The labels may not contain this token for this entity.';
  } else if (bookingSampleIds.length === 0) {
    verdictLine = 'No Booking.com reservations in Uplisting sample. Cannot compute overlap. ' +
      'Check if any Booking.com reservations exist for this entity, or if the problem is data sync.';
  }

  return (
    <div className="border rounded-lg p-3">
      <button
        className="flex items-center gap-1 text-sm font-semibold w-full text-left"
        onClick={() => setOpen(!open)}
      >
        {open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
        Channel-ref trace (Booking.com)
      </button>
      {open && (
        <div className="mt-2 space-y-2">
          {pool_empty ? (
            <p className="text-xs text-muted-foreground">No Booking.com transactions in window</p>
          ) : (
            <>
              <p className="text-xs font-mono bg-muted/50 rounded px-2 py-1">{summaryParts}</p>

              {/* Token-identity probe: side-by-side */}
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 mt-2">
                {/* Left: per-transaction label scan (2/3 width) */}
                <div className="lg:col-span-2 overflow-x-auto">
                  <h4 className="text-xs font-semibold mb-1">Bank label tokens (per transaction)</h4>
                  <table className="text-xs w-full border-collapse">
                    <thead>
                      <tr className="bg-muted/50">
                        <th className="px-2 py-1 text-left">Txn ID</th>
                        <th className="px-2 py-1 text-left">Raw Label</th>
                        <th className="px-2 py-1 text-left">Parsed Ref</th>
                        <th className="px-2 py-1 text-left">ID Segment</th>
                        <th className="px-2 py-1 text-left">Reservation</th>
                        <th className="px-2 py-1 text-left">Invoice</th>
                        <th className="px-2 py-1 text-center">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {trace.map((row, i) => (
                        <tr key={i} className="border-t border-muted">
                          <td className="px-2 py-1 font-mono">{row.txn_id}</td>
                          <td className="px-2 py-1 max-w-[400px] break-all whitespace-normal">
                            {row.raw_label || '-'}
                          </td>
                          <td className="px-2 py-1 font-mono">{row.parsed_ref ?? '-'}</td>
                          <td className="px-2 py-1 font-mono text-blue-600 dark:text-blue-400">
                            {row.id_segment ?? '-'}
                          </td>
                          <td className="px-2 py-1 font-mono text-[10px]">{row.reservation_match ?? '-'}</td>
                          <td className="px-2 py-1 font-mono">{row.invoice_match ?? '-'}</td>
                          <td className="px-2 py-1 text-center">
                            <Badge className={`text-[10px] ${_STATUS_COLORS[row.status] || 'bg-gray-500 text-white'}`}>
                              {row.status}
                            </Badge>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Right: Uplisting external_reservation_id sample (1/3 width) */}
                <div>
                  <h4 className="text-xs font-semibold mb-1">
                    Uplisting external_reservation_id sample
                    {uplistingSample && !uplistingSample.empty && (
                      <span className="font-normal text-muted-foreground ml-1">
                        ({uplistingSample.total_reservations} total)
                      </span>
                    )}
                  </h4>
                  {!uplistingSample ? (
                    <p className="text-xs text-muted-foreground">Not loaded</p>
                  ) : uplistingSample.empty ? (
                    <p className="text-xs text-red-600 dark:text-red-400">
                      No uplisting_reservations for entity {uplistingSample.entity_id}.
                      This means the problem is data sync, not parsing.
                    </p>
                  ) : (
                    <table className="text-xs w-full border-collapse">
                      <thead>
                        <tr className="bg-muted/50">
                          <th className="px-2 py-1 text-left">ext_reservation_id</th>
                          <th className="px-2 py-1 text-left">uplisting_id</th>
                          <th className="px-2 py-1 text-left">channel</th>
                        </tr>
                      </thead>
                      <tbody>
                        {uplistingSample.sample.map((row, i) => (
                          <tr key={i} className="border-t border-muted">
                            <td className="px-2 py-1 font-mono text-blue-600 dark:text-blue-400">
                              {row.external_reservation_id != null
                                ? String(row.external_reservation_id)
                                : '-'}
                            </td>
                            <td className="px-2 py-1 font-mono">{row.uplisting_id ?? '-'}</td>
                            <td className="px-2 py-1">{row.channel ?? '-'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              </div>

              {/* Verdict summary */}
              {verdictLine && (
                <div className="bg-blue-50 dark:bg-blue-950/30 border border-blue-300 rounded p-2 mt-2">
                  <p className="text-xs font-semibold">Token-identity verdict</p>
                  <p className="text-xs font-mono mt-1">{verdictLine}</p>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

function AirbnbBatchTraceSection({ trace: data }) {
  const [open, setOpen] = useState(false);

  if (!data) return null;
  const { trace = [], status_counts = {}, invoice_pool_size, pool_empty, payout_not_in_feed = [] } = data;

  const summaryParts = Object.entries(status_counts)
    .map(([k, v]) => `${k}: ${v}`)
    .join('  ');

  return (
    <div className="border rounded-lg p-3">
      <button
        className="flex items-center gap-1 text-sm font-semibold w-full text-left"
        onClick={() => setOpen(!open)}
      >
        {open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
        Airbnb batch trace
      </button>
      {open && (
        <div className="mt-2 space-y-2">
          {pool_empty ? (
            <p className="text-xs text-muted-foreground">
              No Airbnb-recognised transactions in window. If a real Airbnb payout is
              expected here, its label likely did not contain "airbnb" -- a recognizer
              miss, invisible to this trace by construction.
            </p>
          ) : (
            <>
              <p className="text-xs font-mono bg-muted/50 rounded px-2 py-1">
                {summaryParts}
                {invoice_pool_size != null && `  |  Airbnb invoice pool: ${invoice_pool_size}`}
              </p>

              {payout_not_in_feed.length > 0 && (
                <div className="border border-amber-300 bg-amber-50 dark:bg-amber-950/30 rounded p-2">
                  <p className="text-xs font-semibold mb-1">
                    Payout not in feed ({payout_not_in_feed.length}) -- open Airbnb invoice,
                    no matching payout has landed in this account's bank feed
                  </p>
                  <table className="text-xs w-full border-collapse">
                    <thead>
                      <tr className="bg-muted/50">
                        <th className="px-2 py-1 text-left">Invoice</th>
                        <th className="px-2 py-1 text-right">Amount</th>
                        <th className="px-2 py-1 text-left">Check-in</th>
                        <th className="px-2 py-1 text-right">Days since check-in</th>
                      </tr>
                    </thead>
                    <tbody>
                      {payout_not_in_feed.map((r, i) => (
                        <tr key={i} className="border-t border-muted">
                          <td className="px-2 py-1 font-mono">
                            {r.invoice_number} (#{r.invoice_id})
                          </td>
                          <td className="px-2 py-1 text-right font-mono">{r.invoice_amount}</td>
                          <td className="px-2 py-1 font-mono">{r.check_in}</td>
                          <td className="px-2 py-1 text-right font-mono">{r.days_since_check_in}d</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              <div className="overflow-x-auto">
                <table className="text-xs w-full border-collapse">
                  <thead>
                    <tr className="bg-muted/50">
                      <th className="px-2 py-1 text-left">Txn ID</th>
                      <th className="px-2 py-1 text-left">Label</th>
                      <th className="px-2 py-1 text-right">Amount</th>
                      <th className="px-2 py-1 text-left">Date</th>
                      <th className="px-2 py-1 text-center">Status</th>
                      <th className="px-2 py-1 text-left">Matched Invoice</th>
                      <th className="px-2 py-1 text-right">Lag</th>
                      <th className="px-2 py-1 text-left">Candidates considered</th>
                      <th className="px-2 py-1 text-left">Reason</th>
                    </tr>
                  </thead>
                  <tbody>
                    {trace.map((row, i) => (
                      <tr key={i} className="border-t border-muted align-top">
                        <td className="px-2 py-1 font-mono">{row.transaction_id}</td>
                        <td className="px-2 py-1 max-w-[280px] break-all whitespace-normal">
                          {row.transaction_label || '-'}
                        </td>
                        <td className="px-2 py-1 text-right font-mono">{row.transaction_amount}</td>
                        <td className="px-2 py-1 font-mono">{row.transaction_date ?? '-'}</td>
                        <td className="px-2 py-1 text-center">
                          <Badge
                            className={`text-[10px] ${
                              _AIRBNB_TRACE_STATUS_COLORS[row.status] || 'bg-gray-500 text-white'
                            }`}
                          >
                            {row.status}
                          </Badge>
                        </td>
                        <td className="px-2 py-1 font-mono">{row.matched_invoice_number || '-'}</td>
                        <td className="px-2 py-1 text-right font-mono">
                          {row.lag_days != null ? `${row.lag_days}d` : '-'}
                        </td>
                        <td className="px-2 py-1 max-w-[260px]">
                          {(row.candidates_considered || []).length === 0 ? (
                            <span className="text-muted-foreground">none</span>
                          ) : (
                            <ul className="space-y-0.5">
                              {row.candidates_considered.map((c, j) => (
                                <li key={j} className="font-mono text-[10px]">
                                  #{c.invoice_id} {c.invoice_amount} (check-in {c.check_in ?? c.invoice_date ?? '-'},
                                  {' '}
                                  {c.delta_days != null ? `${c.delta_days}d` : '?'})
                                </li>
                              ))}
                            </ul>
                          )}
                        </td>
                        <td className="px-2 py-1 max-w-[320px] break-words whitespace-normal">
                          {row.reason || (row.status === 'matched' ? row.match_field : '-')}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

function ScopeStatusBadge({ result }) {
  const status = result.scope_status;
  if (status === 'ok') {
    return <Badge className="bg-green-600 text-white">Scope: OK</Badge>;
  }
  if (status === 'missing_transactions_scope' || status === 'missing_invoices_scope') {
    return (
      <Badge className="bg-orange-500 text-white">
        Scope: Missing ({status.replace('missing_', '').replace('_scope', '')})
      </Badge>
    );
  }
  if (status === 'token_invalid') {
    return <Badge className="bg-red-600 text-white">Token: Invalid</Badge>;
  }
  return <Badge variant="outline">{status}</Badge>;
}

function ShapeBadge({ shape }) {
  if (shape === 'per_booking' || shape === '1to1') {
    return <Badge variant="outline" className="text-[10px]">1:1</Badge>;
  }
  if (shape === 'batch') {
    return <Badge className="bg-purple-600 text-white text-[10px]">batch</Badge>;
  }
  return <Badge variant="outline" className="text-[10px]">{shape}</Badge>;
}

function ConfidenceBadge({ confidence }) {
  if (confidence === 'high') {
    return <Badge className="bg-green-600 text-white text-[10px]">high</Badge>;
  }
  if (confidence === 'low') {
    return <Badge className="bg-orange-500 text-white text-[10px]">low</Badge>;
  }
  if (confidence === 'ambiguous') {
    return <Badge className="bg-red-600 text-white text-[10px]">ambiguous</Badge>;
  }
  return <Badge variant="outline" className="text-[10px]">{confidence}</Badge>;
}

function MetricCard({ label, value, sub }) {
  return (
    <div className="border rounded-lg p-2">
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className="text-lg font-semibold">{value}</p>
      {sub && <p className="text-[10px] text-muted-foreground">{sub}</p>}
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="border rounded-lg p-3">
      <h3 className="text-sm font-semibold mb-2">{title}</h3>
      {children}
    </div>
  );
}

function CollapsibleSection({ title, children }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border rounded-lg p-3">
      <button
        className="flex items-center gap-1 text-sm font-semibold w-full text-left"
        onClick={() => setOpen(!open)}
      >
        {open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
        {title}
      </button>
      {open && <div className="mt-2">{children}</div>}
    </div>
  );
}
